import { Component, computed, OnInit, signal } from '@angular/core';
import { RolesProxyService } from '../../../proxies/roles.proxy.service';
import { GroupProxyService } from '../../../proxies/group.proxy.service';
import { ModalService } from '@card-software/library';
import { Role } from '../../../models/user-manager/roles';
import { Group } from '../../../models/user-manager/group';
import { UserSetRolesComponent } from '../../modals/user-set-roles/user-set-roles.component';
import { UserSetGroupsComponent } from '../../modals/user-set-groups/user-set-groups.component';
import { isDelegatedUserManager } from '../../../utilities/delegate-role.util';
import { AccessType } from '../../../models/user-manager/access-type';
import { AccessTypeLabel, accessTypeLabel } from '../../../models/user-manager/access-type-labels';

@Component({
  selector: 'app-user-details',
  templateUrl: './user-details.component.html',
  styleUrl: './user-details.component.scss',
})
export class UserDetailsComponent implements OnInit {
  isLoading = 0;
  listGroups = signal<Group[]>([]);
  listRoles = signal<Role[]>([]);
  listSortGroups = signal<Group[]>([]);
  listSortRoles = signal<Role[]>([]);
  /**
   * Delegated user managers have no route here (defensive): the API answers them only delegable groups and refuses
   * roles (#6261), so roles and the group/role editors are hidden (#6263). Holding the admin role too makes them
   * internal (#6295).
   */
  readonly isDelegate = computed(() => isDelegatedUserManager());
  constructor(
    private rolesProxyService: RolesProxyService,
    private groupProxyService: GroupProxyService,
    private modalService: ModalService,
  ) {}

  ngOnInit(): void {
    this.getGroupInfo();
    if (!this.isDelegate()) {
      this.getRolesInfo();
    }
  }

  typeLabel(type: AccessType | undefined): AccessTypeLabel {
    return accessTypeLabel(type);
  }

  /** "External managers" column: Global groups can never be visible to them. */
  isGlobal(group: Group): boolean {
    return group.type === AccessType.Global;
  }

  private getGroupInfo() {
    this.isLoading++;
    this.groupProxyService.getAllGroups().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.listGroups.set(response.data);
          this.listSortGroups.set(response.data);
        }
        this.isLoading--;
      },
      error: (error) => {
        console.log('error', error);
        this.isLoading--;
      },
    });
  }

  private getRolesInfo() {
    this.isLoading++;
    this.rolesProxyService.getAllRoles().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.listRoles.set(response.data);
          this.listSortRoles.set(response.data);
        }
        this.isLoading--;
      },
      error: (error) => {
        console.log('error', error);
        this.isLoading--;
      },
    });
  }

  openModalRoles(role: Role | null) {
    this.modalService.open(UserSetRolesComponent, { modalClass: 'modal_small' }, { role }).subscribe(() => {
      this.getRolesInfo();
    });
  }

  openModalGroups(group: Group | null) {
    this.modalService.open(UserSetGroupsComponent, { modalClass: 'modal_small' }, { group }).subscribe(() => {
      this.getGroupInfo();
    });
  }

  sortData(event: Event, type: string) {
    const term = (event.target as HTMLInputElement).value;

    if (term === null) return;

    if (type === 'groups') {
      this.listSortGroups.set(this.listGroups().filter((group) => group.name!.toLowerCase().includes(term.toLowerCase())));
      if (term == '') {
        this.listSortGroups.set(this.listGroups());
      }
    } else if (type === 'roles') {
      this.listSortRoles.set(this.listRoles().filter((role) => role.name!.toLowerCase().includes(term.toLowerCase())));
      if (term == '') {
        this.listSortRoles.set(this.listRoles());
      }
    }
  }
}
